/**
 * Realtime Section
 *
 * Live event stream + active users counter with CSS animations.
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Radio, Users, Zap, Clock } from 'lucide-react';
import type { RealtimeData, StreamEvent } from '../../../types/analytics';

interface RealtimeSectionProps {
  realtime: RealtimeData | null;
  streamEvents: StreamEvent[];
  loading: boolean;
}

const EVENT_CATEGORY_COLORS: Record<string, string> = {
  auth: 'bg-blue-100 text-blue-700',
  content: 'bg-purple-100 text-purple-700',
  conversion: 'bg-emerald-100 text-emerald-700',
  engagement: 'bg-amber-100 text-amber-700',
  error: 'bg-red-100 text-red-700',
  navigation: 'bg-cyan-100 text-cyan-700',
  performance: 'bg-indigo-100 text-indigo-700',
};

export const RealtimeSection: React.FC<RealtimeSectionProps> = ({
  realtime,
  streamEvents,
}) => {
  return (
    <div className="space-y-4">
      {/* Live Indicator + Stats */}
      <div className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <div className="relative">
            <Radio className="w-4 h-4 text-red-500" />
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full animate-ping" />
          </div>
          <h3 className="text-sm font-semibold text-gray-700">Live</h3>
          <span className="text-[10px] text-gray-400 ml-auto">Last 5 min</span>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-10 h-10 bg-emerald-100 rounded-xl mb-1">
              <Users className="w-5 h-5 text-emerald-600" />
            </div>
            <p className="text-xl font-extrabold text-gray-900">
              {realtime?.active_users || 0}
            </p>
            <p className="text-[10px] text-gray-400">Active Users</p>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-10 h-10 bg-blue-100 rounded-xl mb-1">
              <Zap className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-xl font-extrabold text-gray-900">
              {realtime?.events_per_min || 0}
            </p>
            <p className="text-[10px] text-gray-400">Events/min</p>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-10 h-10 bg-purple-100 rounded-xl mb-1">
              <Clock className="w-5 h-5 text-purple-600" />
            </div>
            <p className="text-xl font-extrabold text-gray-900">
              {realtime?.events_last_5min || 0}
            </p>
            <p className="text-[10px] text-gray-400">Total (5min)</p>
          </div>
        </div>
      </div>

      {/* Live Event Stream */}
      <div className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Event Stream</h3>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          <AnimatePresence initial={false}>
            {/* Show stream events first, then realtime latest_events */}
            {(streamEvents.length > 0
              ? streamEvents
              : realtime?.latest_events || []
            )
              .slice(0, 20)
              .map((event, idx) => {
                const name = event.event_name || 'unknown';
                const category = event.category || '';
                const colorClass =
                  EVENT_CATEGORY_COLORS[category] || 'bg-gray-100 text-gray-700';

                return (
                  <motion.div
                    key={`${name}-${idx}-${event.timestamp}`}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, height: 0 }}
                    className="flex items-center gap-2 py-1.5 border-b border-gray-50 last:border-0"
                  >
                    <span
                      className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${colorClass}`}
                    >
                      {category}
                    </span>
                    <span className="text-xs text-gray-700 flex-1 truncate">{name}</span>
                    {event.device && (
                      <span className="text-[10px] text-gray-400">{event.device}</span>
                    )}
                    {event.timestamp && (
                      <span className="text-[10px] text-gray-300">
                        {new Date(event.timestamp).toLocaleTimeString('en', {
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                        })}
                      </span>
                    )}
                  </motion.div>
                );
              })}
          </AnimatePresence>

          {!streamEvents.length && !realtime?.latest_events?.length && (
            <p className="text-xs text-gray-400 text-center py-4">
              No events yet. Events will appear here in real-time.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
